<script setup>
import { computed } from 'vue'
import { useSearchStore } from '@/stores/SearchStore'
import { useBatchStore } from '@/stores/BatchStore'
import { CARD_TYPES } from '@/constants'
import BatchPreviewStack from '@/components/Batches/BatchPreviewStack.vue'
import placeCard1 from '@/assets/images/place-cards/1.png'
import placeCard2 from '@/assets/images/place-cards/2.png'
import placeCard3 from '@/assets/images/place-cards/3.png'
import moment from 'moment'

const props = defineProps({
  active: { type: Boolean, default: false },
  item: { type: Object, required: true },
  selectedCard: { type: Object, default: null }
})

const searchStore = useSearchStore()
const batchStore = useBatchStore()

/**
 * The generic card art, shaped like `preview_cards` so it can go through the
 * SAME component. A batch with no previews then draws an identical fanned
 * stack — same rotation, same size — instead of a differently-stacked
 * placeholder, so a results list reads as one row style rather than two.
 */
const PLACEHOLDER_CARDS = [
  { card_front_image: placeCard1 },
  { card_front_image: placeCard2 },
  { card_front_image: placeCard3 },
]

// Roughly one batch in six comes back with no `preview_cards` (measured against
// the live endpoint: 21 of 25 had them), so this path is not an edge case.
const previewCards = computed(() => {
  const cards = props.item?.preview_cards

  return cards?.length ? cards : PLACEHOLDER_CARDS
})

</script>
<template>
<li
  class="flex items-center justify-between p-3 transition duration-300 hover:bg-blue-50 cursor-pointer group"
  :class="{ 'bg-blue-50': props.active, 'bg-gray-200 pointer-events-none cursor-default': props.selectedCard && ((!batchStore.selectedBatch?.lock_id && props.item?.lock_id) || (!batchStore.selectedBatch?.locked_sets?.length && props.item?.locked_sets?.length)) }"
>
  <div class="flex items-center gap-x-3 w-full">
    <!--
      The batch's OWN cards, not three generic placeholders.

      `GET /batches` returns `preview_cards` ({ card_id, card_front_image }) on
      every batch that has any, and this row is how a dealer picks between
      batches with similar names — three identical stock images told them
      nothing. `BatchPreviewStack` is the same fanned illustration the All
      Batches panel and the Inventory batch chips draw. A batch with no previews
      falls back to generic card art fed through the SAME stack, so every row in
      the list has the same shape.
    -->
    <BatchPreviewStack :cards="previewCards" class="w-9 h-12 flex-shrink-0" />
    <div class="flex-auto">
      <h3 class="font-medium">{{ props.item.name }}</h3>
      <p class="text-gray-500">{{CARD_TYPES[props.item.type]}} Card . Imported {{ moment(props.item.created_at).format('MMMM DD YYYY . h:mm:ss A') }}</p>
    </div>
    <div 
      v-if="props.selectedCard && ((!batchStore.selectedBatch?.lock_id && props.item?.lock_id) || (!batchStore.selectedBatch?.locked_sets?.length && props.item?.locked_sets?.length))"
      class="py-1 px-2 bg-blue-100 rounded-xl"
    >
      <span class='text-gray-500 text-[10px]'>Lock Set Batch</span>
    </div>
    <button
      v-if="searchStore.cdp_search.locationOfSearchCall === 'add-button'"
      class="
        alternative-button text-xs py-1 text-tint-1 focus:ring-tint-1/20 transition duration-300 opacity-0
        group-hover:opacity-100
      "
      :class="{'!opacity-100': props.active}"
    >
      Add Cards
    </button>
    <button
      v-else-if="props.selectedCard && !props.item?.lock_id && !props.item?.locked_sets?.length"
      class="
        alternative-button text-xs py-1 text-tint-1 focus:ring-tint-1/20 transition duration-300 opacity-0
        group-hover:opacity-100
      "
      :class="{'!opacity-100': props.active}"
    >
      Add Selected Card
    </button>
  </div>
</li>
</template>